import echoProphetIcon from "../../../assets/icons/shill-dna-echo_prophet.png";
import emergingDegenIcon from "../../../assets/icons/shill-dna-emerging_degen.png";
import kingOfTheShillIcon from "../../../assets/icons/shill-dna-king_of_the_shill.png";
import narrativeMageIcon from "../../../assets/icons/shill-dna-narrative_mage.png";
import pathFinderIcon from "../../../assets/icons/shill-dna-path_finder.png";
import precisionSniperIcon from "../../../assets/icons/shill-dna-precision_sniper.png";
import marketMoverIcon from "../../../assets/icons/shill-dna-market_mover.png";

import earlyScoreIcon from "../../../assets/icons/shill-dna-attr-early.png";
import accuracyScoreIcon from "../../../assets/icons/shill-dna-attr-accuracy.png";
import socialScoreIcon from "../../../assets/icons/shill-dna-attr-social.png";
import pumpScoreIcon from "../../../assets/icons/shill-dna-attr-pump.png";
import insightScoreIcon from "../../../assets/icons/shill-dna-attr-insight.png";

export type DNAClass =
  | "ECHO_PROPHET"
  | "EMERGING_DEGEN"
  | "KING_OF_THE_SHILL"
  | "NARRATIVE_MAGE"
  | "PATH_FINDER"
  | "PRECISION_SNIPER"
  | "MARKET_MOVER";

export type DNAAttribute =
  | "earlyScore"
  | "accuracyScore"
  | "socialScore"
  | "pumpScore"
  | "insightScore";

// export interface TraitScore {
//   accuracyScore: number;
//   pumpScore: number;
//   socialScore: number;
//   earlyScore: number;
//   insightScore: number;
// }

export interface DNAClassConfig {
  key: DNAClass;
  name: string;
  subtitle: string;
  description: string;
  icon: React.ReactNode;
}

export interface DNAAttributeConfig {
  name: string;
  icon: string;
}

export const SCORE_LEVEL = {
  VERY_HIGH: 200,
  HIGH: 150,
  MEDIUM: 100,
  LOW: 50,
  VERY_LOW: 0,
};

export const MAX_TRAIT_SCORE = SCORE_LEVEL.VERY_HIGH + SCORE_LEVEL.VERY_HIGH - SCORE_LEVEL.HIGH;

export const DNA_ATTRIBUTE_CONFIG_MAP: Record<DNAAttribute, DNAAttributeConfig> = {
  earlyScore: {
    name: "Early",
    icon: earlyScoreIcon,
  },
  accuracyScore: {
    name: "Accuracy",
    icon: accuracyScoreIcon,
  },
  socialScore: {
    name: "Social",
    icon: socialScoreIcon,
  },
  pumpScore: {
    name: "Volume",
    icon: pumpScoreIcon,
  },
  insightScore: {
    name: "Insight",
    icon: insightScoreIcon,
  },
};

export const DNA_CLASS_CONFIG_MAP: Record<DNAClass, DNAClassConfig> = {
  KING_OF_THE_SHILL: {
    key: "KING_OF_THE_SHILL",
    name: "King of the Shill",
    subtitle: "The Apex Caller",
    description:
      "Master of all five traits — early, accurate, social, pump-driving, and insightful. A complete caller who dominates across the spectrum.",
    icon: <img rel="preload" src={kingOfTheShillIcon} alt="dna" width={56} height={56} />,
  },
  ECHO_PROPHET: {
    key: "ECHO_PROPHET",
    name: "Echo Prophet",
    subtitle: "What They Say, Moves Crowds",
    description:
      "Commands attention and engagement. Amplifies narratives, drives visibility, and spreads alpha fast across networks.",
    icon: <img rel="preload" src={echoProphetIcon} alt="dna" width={56} height={56} />,
  },
  EMERGING_DEGEN: {
    key: "EMERGING_DEGEN",
    name: "Emerging Degen",
    subtitle: "Still Early",
    description:
      "A newcomer to the shill game. They’re experimenting and learning the ropes — but haven’t yet proven themselves . Every top caller started here.",
    icon: <img rel="preload" src={emergingDegenIcon} alt="dna" width={56} height={56} />,
  },
  NARRATIVE_MAGE: {
    key: "NARRATIVE_MAGE",
    name: "Narrative Mage",
    subtitle: "Where Conviction Begins",
    description:
      "Crafts deep, fundamental narratives. Connects themes, projects, and market meta into compelling calls that influence long-term positioning.",
    icon: <img rel="preload" src={narrativeMageIcon} alt="dna" width={56} height={56} />,
  },
  PATH_FINDER: {
    key: "PATH_FINDER",
    name: "Path Finder",
    subtitle: "Before the Crowd",
    description:
      "Identifies high-potential tokens before they gain traction. Often first to drop calls that later trend across the market.",
    icon: <img rel="preload" src={pathFinderIcon} alt="dna" width={56} height={56} />,
  },
  PRECISION_SNIPER: {
    key: "PRECISION_SNIPER",
    name: "Precision Sniper",
    subtitle: "One Shot, One Moon",
    description:
      "Focuses on high win-rate, low noise calls. Prioritizes precision and consistent profit over hype.",
    icon: <img rel="preload" src={precisionSniperIcon} alt="dna" width={56} height={56} />,
  },
  MARKET_MOVER: {
    key: "MARKET_MOVER",
    name: "Market Mover",
    subtitle: "Capital moves when they do",
    description:
      "Triggers real trading volume. Their calls drive liquidity and influence token price action.",
    icon: <img rel="preload" src={marketMoverIcon} alt="dna" width={56} height={56} />,
  },
};

export const getDNAClass = (traitScores: Record<DNAAttribute, number>): DNAClassConfig => {
  // King of the Shill
  if (
    traitScores.socialScore >= SCORE_LEVEL.HIGH &&
    traitScores.accuracyScore >= SCORE_LEVEL.HIGH &&
    traitScores.pumpScore >= SCORE_LEVEL.HIGH &&
    traitScores.insightScore >= SCORE_LEVEL.HIGH &&
    traitScores.earlyScore >= SCORE_LEVEL.HIGH
  ) {
    return DNA_CLASS_CONFIG_MAP.KING_OF_THE_SHILL;
  }

  // Emerging Degen
  if (
    traitScores.socialScore < SCORE_LEVEL.MEDIUM &&
    traitScores.accuracyScore < SCORE_LEVEL.MEDIUM &&
    traitScores.pumpScore < SCORE_LEVEL.MEDIUM &&
    traitScores.insightScore < SCORE_LEVEL.MEDIUM &&
    traitScores.earlyScore < SCORE_LEVEL.MEDIUM
  ) {
    return DNA_CLASS_CONFIG_MAP.EMERGING_DEGEN;
  }

  const { key, value } = Object.entries(traitScores).reduce(
    (max, [key, value]): { key: DNAAttribute; value: number } => {
      // if (key === "insightScore") return { key: max.key, value: max.value };
      return value > max.value
        ? { key: key as DNAAttribute, value }
        : { key: max.key, value: max.value };
    },
    { key: "socialScore" as DNAAttribute, value: -Infinity },
  );

  if (key === "insightScore" && value >= SCORE_LEVEL.MEDIUM) {
    return DNA_CLASS_CONFIG_MAP.NARRATIVE_MAGE;
  }

  if (key === "socialScore" && value >= SCORE_LEVEL.MEDIUM) {
    return DNA_CLASS_CONFIG_MAP.ECHO_PROPHET;
  }

  if (key === "earlyScore" && value >= SCORE_LEVEL.MEDIUM) {
    return DNA_CLASS_CONFIG_MAP.PATH_FINDER;
  }

  if (key === "accuracyScore" && value >= SCORE_LEVEL.MEDIUM) {
    return DNA_CLASS_CONFIG_MAP.PRECISION_SNIPER;
  }

  return DNA_CLASS_CONFIG_MAP.MARKET_MOVER;
};

export const getScoreLevel = (score: number) => {
  if (score >= SCORE_LEVEL.VERY_HIGH) {
    return "Very High";
  }

  if (score >= SCORE_LEVEL.HIGH) {
    return "High";
  }

  if (score >= SCORE_LEVEL.MEDIUM) {
    return "Medium";
  }

  if (score >= SCORE_LEVEL.LOW) {
    return "Low";
  }

  return "Very Low";
};
